import { appAlert } from "./appDialogs";
// Inspection.js
// Inspection workbench: pharmacy selector at the top, then a tabbed form. Tabs
// are Staff, SOPs/PGDs, Pharmacy Management, Safety report, Complaints and
// feedback, Visitor log, Periodic checks.
//
// Riverside Pharmacy is populated with mock data so each tab renders real-looking
// content; other pharmacies (and any tab without data) fall back to a placeholder.
//
// NOTE: data is hard-coded mock for now — no API calls yet.

import React, { useState } from "react";

const PLACEHOLDER_PHARMACIES = [
  "Riverside Pharmacy",
  "Highstreet Chemist",
  "Parkside Pharmacy",
  "Canalside Pharmacy",
  "Meadow Pharmacy",
  "Station Pharmacy",
  "Orchard Pharmacy"
];

const TABS = [
  "Staff",
  "SOPs/PGDs",
  "Reading grid",
  "Pharmacy Management",
  "Safety report",
  "Complaints and feedback",
  "Visitor log",
  "Periodic checks"
];

/* ---------------- Mock inspection data ----------------
   Keyed by pharmacy name. Only Riverside is populated for now. */
const INSPECTION_DATA = {
  "Riverside Pharmacy": {
    staff: [
      { name: "Aisha Khan",     role: "Pharmacist (RP)",      gphc: "2071234", dbs: "2024-03-11", training: "Up to date",      lastAppraisal: "2025-09-12" },
      { name: "Tom Bradley",    role: "Dispenser",            gphc: "—",       dbs: "2023-11-02", training: "Up to date",      lastAppraisal: "2024-11-20" },
      { name: "Megan Lloyd",    role: "Counter Assistant",    gphc: "—",       dbs: "2025-01-15", training: "MCA in progress", lastAppraisal: "None on record" }
    ],
    sopsPgds: [
      { ref: "SOP-01", title: "Dispensing process",            version: "v4.2", reviewed: "2025-08-01", due: "2026-08-01", status: "Current" },
      { ref: "SOP-07", title: "Handling of CDs",               version: "v3.0", reviewed: "2025-02-14", due: "2026-02-14", status: "Current" },
      { ref: "SOP-12", title: "Responding to symptoms (OTC)",  version: "v2.5", reviewed: "2024-05-09", due: "2025-05-09", status: "Overdue" },
      { ref: "PGD-04", title: "Flu vaccination (NHS)",         version: "v1.3", reviewed: "2025-09-01", due: "2026-09-01", status: "Current" },
      { ref: "PGD-09", title: "Oseltamivir supply",            version: "v1.0", reviewed: "2024-10-20", due: "2025-10-20", status: "Overdue" }
    ],
    // Which staff have read which SOP/PGD. Keyed by SOP ref -> { staffName: true/false }.
    readingGrid: {
      "SOP-01": { "Aisha Khan": true,  "Tom Bradley": true,  "Megan Lloyd": true  },
      "SOP-07": { "Aisha Khan": true,  "Tom Bradley": true,  "Megan Lloyd": false },
      "SOP-12": { "Aisha Khan": true,  "Tom Bradley": false, "Megan Lloyd": false },
      "PGD-04": { "Aisha Khan": true,  "Tom Bradley": false, "Megan Lloyd": false },
      "PGD-09": { "Aisha Khan": true,  "Tom Bradley": true,  "Megan Lloyd": true  }
    },
    management: {
      responsiblePharmacist: "Aisha Khan (GPhC 2071234)",
      superintendent: "S. Patel",
      premisesRegistration: "1099887 — registered",
      professionalIndemnity: "Valid to 2026-04-30",
      nhsContract: "Active (ODS FQ123)",
      lastSelfAssessment: "2025-10-02",
      notes: "Premises in good order. Consultation room compliant. Fridge logs reviewed — all in range last 30 days."
    },
    safety: {
      patientSafetyIncidents12mo: 2,
      nearMisses12mo: 14,
      lfpseReported: 2,
      lastIncident: "2026-04-22",
      summary: "Two reportable incidents in the last 12 months, both wrong-strength near-miss escalations caught at the point of handout. Root cause: lookalike packaging. Actions: shelf re-organisation and second-check reinforced.",
      openActions: [
        { action: "Relocate lookalike statins to separate shelf bays", owner: "Tom Bradley", due: "2026-06-15", status: "In progress" },
        { action: "Re-brief team on bagging-stage final check", owner: "Aisha Khan", due: "2026-06-05", status: "Open" }
      ]
    },
    complaints: [
      { date: "2026-03-18", type: "Complaint",  summary: "Prescription not ready at promised time.", outcome: "Apology given, process reviewed.", status: "Closed" },
      { date: "2026-01-09", type: "Feedback",   summary: "Praise for delivery driver's helpfulness.", outcome: "Passed to staff member.",        status: "Closed" },
      { date: "2025-11-27", type: "Complaint",  summary: "Difficulty getting through by phone.",       outcome: "Added midday phone cover.",         status: "Closed" }
    ],
    visitors: [
      { date: "2026-05-14", name: "J. Hughes",  organisation: "ICB Pharmacy Team", purpose: "Routine assurance visit", in: "10:05", out: "11:20" },
      { date: "2026-02-03", name: "R. Singh",   organisation: "Wholesaler (AAH)",  purpose: "CD cabinet servicing",   in: "14:30", out: "15:10" },
      { date: "2025-12-11", name: "L. Carter",  organisation: "GPhC",              purpose: "Inspection",             in: "09:15", out: "13:40" }
    ],
    periodic: [
      { check: "Fridge temperature log",   frequency: "Daily",     last: "2026-06-02", status: "On track" },
      { check: "CD running balance check", frequency: "Weekly",    last: "2026-05-30", status: "On track" },
      { check: "Date-checking rota",       frequency: "Monthly",   last: "2026-05-12", status: "On track" },
      { check: "Cleaning schedule sign-off", frequency: "Weekly",  last: "2026-05-25", status: "On track" },
      { check: "Emergency equipment check", frequency: "Quarterly", last: "2026-01-20", status: "Overdue" }
    ]
  }
};

/* ---------------- Shared styles ---------------- */
const th = {
  textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4, color: "#5D5D5D",
  borderBottom: "2px solid #F0F0F0", whiteSpace: "nowrap"
};
const td = { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", verticalAlign: "top" };

function statusPill(status) {
  const s = (status || "").toLowerCase();
  let bg = "#F0F0F0", color = "#434343";
  if (s.includes("overdue") || s === "open") { bg = "#FFF1F0"; color = "#820014"; }
  else if (s.includes("progress")) { bg = "#FFF7E6"; color = "#AD4E00"; }
  else if (s.includes("current") || s.includes("track") || s.includes("closed") || s.includes("up to date")) { bg = "#F6FFED"; color = "#389E0D"; }
  return (
    <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
}

function SimpleTable({ columns, rows, renderCell }) {
  return (
    <div style={{ overflowX: "auto", border: "1px solid #F0F0F0", borderRadius: 8, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ background: "#FAFAFA" }}>
          <tr>{columns.map(c => <th key={c.key} style={th}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map(c => <td key={c.key} style={td}>{renderCell ? renderCell(c.key, r[c.key], r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Per-tab renderers ---------------- */
function StaffSection({ data }) {
  return (
    <SimpleTable
      columns={[
        { key: "name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "gphc", label: "GPhC #" },
        { key: "dbs", label: "DBS date" },
        { key: "training", label: "Training" },
        { key: "lastAppraisal", label: "Last appraisal" }
      ]}
      rows={data.staff}
      renderCell={(key, val) => key === "training" ? statusPill(val) : val}
    />
  );
}

function SopsPgdsSection({ data }) {
  // Placeholder action — no document store wired yet.
  const handleView = (row) => {
    appAlert(`View ${row.ref} — ${row.title} (${row.version})\n\n(Document viewer not yet wired up.)`);
  };
  return (
    <div style={{ overflowX: "auto", border: "1px solid #F0F0F0", borderRadius: 8, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ background: "#FAFAFA" }}>
          <tr>
            <th style={th}>Ref</th>
            <th style={th}>Title</th>
            <th style={th}>Version</th>
            <th style={th}>Last reviewed</th>
            <th style={th}>Next due</th>
            <th style={th}>Status</th>
            <th style={{ ...th, textAlign: "center" }}>Document</th>
          </tr>
        </thead>
        <tbody>
          {data.sopsPgds.map((row, i) => (
            <tr key={i}>
              <td style={td}>{row.ref}</td>
              <td style={td}>{row.title}</td>
              <td style={td}>{row.version}</td>
              <td style={td}>{row.reviewed}</td>
              <td style={td}>{row.due}</td>
              <td style={td}>{statusPill(row.status)}</td>
              <td style={{ ...td, textAlign: "center" }}>
                <button
                  onClick={() => handleView(row)}
                  style={{
                    fontSize: 12, padding: "4px 10px", border: "1px solid #8C8C8C",
                    borderRadius: 4, background: "#fff", cursor: "pointer", whiteSpace: "nowrap"
                  }}
                >
                  View SOP
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReadingGridSection({ data }) {
  const sops = data.sopsPgds || [];
  const grid = data.readingGrid || {};
  // Staff list = the keys present across the reading-grid entries, in stable order.
  const staffNames = (data.staff || []).map(s => s.name);

  const Tick = () => <span style={{ color: "#389E0D", fontWeight: 700, fontSize: 15 }}>✓</span>;
  const Cross = () => <span style={{ color: "#CF1322", fontWeight: 700, fontSize: 15 }}>✗</span>;

  return (
    <div style={{ overflowX: "auto", border: "1px solid #F0F0F0", borderRadius: 8, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ background: "#FAFAFA" }}>
          <tr>
            <th style={th}>SOP / PGD</th>
            {staffNames.map(name => (
              <th key={name} style={{ ...th, textAlign: "center" }}>{name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sops.map(sop => {
            const reads = grid[sop.ref] || {};
            return (
              <tr key={sop.ref}>
                <td style={td}>
                  <span style={{ fontWeight: 600 }}>{sop.ref}</span>
                  <span style={{ color: "#5D5D5D" }}> — {sop.title}</span>
                </td>
                {staffNames.map(name => (
                  <td key={name} style={{ ...td, textAlign: "center" }}>
                    {reads[name] ? <Tick /> : <Cross />}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ManagementSection({ data }) {
  const m = data.management;
  const rows = [
    ["Responsible pharmacist", m.responsiblePharmacist],
    ["Superintendent", m.superintendent],
    ["Premises registration", m.premisesRegistration],
    ["Professional indemnity", m.professionalIndemnity],
    ["NHS contract", m.nhsContract],
    ["Last self-assessment", m.lastSelfAssessment]
  ];
  return (
    <div>
      <div style={{ border: "1px solid #F0F0F0", borderRadius: 8, background: "#fff", overflow: "hidden", marginBottom: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td style={{ ...td, width: 220, fontWeight: 600, color: "#434343", background: "#FAFAFA" }}>{k}</td>
                <td style={td}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 13, color: "#434343", lineHeight: 1.6 }}>
        <strong>Notes:</strong> {m.notes}
      </div>
    </div>
  );
}

function SafetySection({ data }) {
  const s = data.safety;
  const Stat = ({ label, value }) => (
    <div style={{ flex: "1 1 140px", border: "1px solid #F0F0F0", borderRadius: 8, padding: 14, background: "#fff" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#262626" }}>{value}</div>
      <div style={{ fontSize: 12, color: "#5D5D5D" }}>{label}</div>
    </div>
  );
  return (
    <div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <Stat label="Patient safety incidents (12mo)" value={s.patientSafetyIncidents12mo} />
        <Stat label="Near misses (12mo)" value={s.nearMisses12mo} />
        <Stat label="LFPSE reported" value={s.lfpseReported} />
        <Stat label="Last incident" value={s.lastIncident} />
      </div>
      <div style={{ fontSize: 13, color: "#434343", lineHeight: 1.6, marginBottom: 16 }}>
        <strong>Summary:</strong> {s.summary}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#5D5D5D", marginBottom: 8 }}>
        Open actions
      </div>
      <SimpleTable
        columns={[
          { key: "action", label: "Action" },
          { key: "owner", label: "Owner" },
          { key: "due", label: "Due" },
          { key: "status", label: "Status" }
        ]}
        rows={s.openActions}
        renderCell={(key, val) => key === "status" ? statusPill(val) : val}
      />
    </div>
  );
}

function ComplaintsSection({ data }) {
  return (
    <SimpleTable
      columns={[
        { key: "date", label: "Date" },
        { key: "type", label: "Type" },
        { key: "summary", label: "Summary" },
        { key: "outcome", label: "Outcome" },
        { key: "status", label: "Status" }
      ]}
      rows={data.complaints}
      renderCell={(key, val) => key === "status" ? statusPill(val) : val}
    />
  );
}

function VisitorSection({ data }) {
  return (
    <SimpleTable
      columns={[
        { key: "date", label: "Date" },
        { key: "name", label: "Visitor" },
        { key: "organisation", label: "Organisation" },
        { key: "purpose", label: "Purpose" },
        { key: "in", label: "In" },
        { key: "out", label: "Out" }
      ]}
      rows={data.visitors}
    />
  );
}

function PeriodicSection({ data }) {
  return (
    <SimpleTable
      columns={[
        { key: "check", label: "Check" },
        { key: "frequency", label: "Frequency" },
        { key: "last", label: "Last completed" },
        { key: "status", label: "Status" }
      ]}
      rows={data.periodic}
      renderCell={(key, val) => key === "status" ? statusPill(val) : val}
    />
  );
}

function Placeholder({ tab, pharmacy }) {
  return (
    <div style={{
      padding: 24, border: "1px dashed #D9D9D9", borderRadius: 8,
      background: "#fafafa", color: "#5D5D5D"
    }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: "#434343", marginBottom: 6 }}>
        {tab}
      </div>
      <div style={{ fontSize: 13 }}>
        {pharmacy
          ? <>No mock data for <strong>{tab}</strong> at <strong>{pharmacy}</strong> yet.</>
          : <>Select a pharmacy to view the <strong>{tab}</strong> section.</>}
      </div>
    </div>
  );
}

/* Map a tab to its renderer, given the pharmacy's data object. */
function renderTab(tab, data, pharmacy) {
  if (!data) return <Placeholder tab={tab} pharmacy={pharmacy} />;
  switch (tab) {
    case "Staff":                   return <StaffSection data={data} />;
    case "SOPs/PGDs":               return <SopsPgdsSection data={data} />;
    case "Reading grid":            return <ReadingGridSection data={data} />;
    case "Pharmacy Management":     return <ManagementSection data={data} />;
    case "Safety report":           return <SafetySection data={data} />;
    case "Complaints and feedback": return <ComplaintsSection data={data} />;
    case "Visitor log":             return <VisitorSection data={data} />;
    case "Periodic checks":         return <PeriodicSection data={data} />;
    default:                        return <Placeholder tab={tab} pharmacy={pharmacy} />;
  }
}

export default function Inspection() {
  const [pharmacy, setPharmacy] = useState("");
  const [activeTab, setActiveTab] = useState(TABS[0]);

  const data = INSPECTION_DATA[pharmacy] || null;

  const selStyle = {
    fontSize: 13, padding: "6px 10px", border: "1px solid #8C8C8C",
    borderRadius: 4, background: "#fff", cursor: "pointer", minWidth: 220
  };

  return (
    <div style={{ padding: "16px 20px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Inspection</h2>
      <div style={{ fontSize: 13, color: "#5D5D5D", marginBottom: 16 }}>
        Branch inspection workbench. Choose a pharmacy, then work through each section.
      </div>

      {/* Pharmacy selector */}
      <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#5D5D5D", marginBottom: 18 }}>
        PHARMACY
        <select value={pharmacy} onChange={e => setPharmacy(e.target.value)} style={selStyle}>
          <option value="">Select a pharmacy…</option>
          {PLACEHOLDER_PHARMACIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>

      {/* Tabbed form */}
      <div style={{ border: "1px solid #F0F0F0", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
        {/* Tab strip */}
        <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #F0F0F0", background: "#FAFAFA" }}>
          {TABS.map(tab => {
            const active = tab === activeTab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "10px 16px", border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  color: active ? "#1D39C4" : "#434343",
                  background: active ? "#fff" : "transparent",
                  borderBottom: active ? "2px solid #4338ca" : "2px solid transparent",
                  marginBottom: -1
                }}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Tab body */}
        <div style={{ padding: 18 }}>
          {renderTab(activeTab, data, pharmacy)}
        </div>
      </div>
    </div>
  );
}